import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import { DataTableColumnType, FilterType } from '@/enums';
import { DataTableColumn, RateUnitMapping } from '@/interfaces';
import { RateUnitMappingsService } from '@/service';


@Component({
    selector: 'app-rate-unit-mapping-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [DataTableComponent],
})
export class RateUnitMappingListingComponent {

    reloadTable$ = new Subject<void>();
    loading = false;
    columns: DataTableColumn<RateUnitMapping>[] = [
        {
            field: 'is_mapped',
            header: 'Mapped',
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            tableVisible: false,
            sortable: false,
        },
        {
            field: 'rate_unit',
            header: 'Rate unit (raw)',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.rateUnitAutocomplete('rate_unit', q, context),
        },
        {
            field: 'count_of_occurrences',
            header: '# of Occurrences',
            filterType: FilterType.NUMERIC,
        },
        {
            field: 'is_excluded',
            header: 'Exclude',
            type: DataTableColumnType.CHECKBOX,
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            onCheckboxChange: (value: boolean, row: RateUnitMapping) => this.edit(row, { is_excluded: value }),
        },
        {
            field: 'rate_unit_mapped',
            header: 'Rate unit (mapped)',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.rateUnitAutocomplete('rate_unit_mapped', q, context),
            editable: true,
            editType: FilterType.TEXT,
            onEditSave: (value, row) => {
                const mapped =
                    typeof value === 'string' ? value.trimEnd() : String(value ?? '').trimEnd();
                this.edit(row, { rate_unit_mapped: mapped });
            }
        },
        {
            field: 'updated_at',
            header: 'Updated Date/Time',
            type: DataTableColumnType.DATETIME,
            filterType: FilterType.DATE,
        },
        {
            field: 'updated_by_user.name',
            header: 'Last Updated By',
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-eye',
                    label: 'View Rates',
                    command: (row) =>
                        this.router.navigate(['/rates'], {
                            queryParams: {
                                rate_unit_mapped: (row.rate_unit_mapped?.trim() || row.rate_unit || '').trim() || undefined
                            }
                        }),
                    showIf: (row) =>
                        this.authService.hasPermission('rate_unit_mappings.rate_unit_mappings.read')
                        && !!row.count_of_occurrences
                },
            ]
        }
    ];

    constructor(
        public authService: AuthService,
        private router: Router,
        private service: RateUnitMappingsService,
        private messageService: MessageService,
    ) {
    }

    loadData = (params: Record<string, any>) => this.service.listing(params);

    exportData = (params: Record<string, any>) => this.service.export(params);

    rateUnitAutocomplete(field: 'rate_unit' | 'rate_unit_mapped', query: string, context: Record<string, any> = {}) {
        return this.service.autocomplete(field, query, context).pipe(map((res) => res.data ?? res));
    }

    edit(row: RateUnitMapping, data: Record<string, any>): void {
        this.loading = true;
        this.service.edit(row.id, data).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'Rate unit mapping has been updated successfully.'
                });
                this.reloadTable$.next();
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }
}
